import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { QuestionHistory, apiService } from '../../services';
import QuestionSuggestions from './QuestionSuggestions';

const InputBar = ({ onSendMessage, onNewChat }) => {
  const [message, setMessage] = useState('');
  const [isLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [questionHistoryInstance] = useState(() => new QuestionHistory());
  const inputRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      return;
    }
    
    // Stop speech recognition if it's active
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    setLocalLoading(true);
    setError(null);
    questionHistoryInstance.add(message.trim());
    
    try {
      const threadId = localStorage.getItem('currentThreadId');
      if (!threadId) {
        throw new Error('No active thread. Please start a new chat.');
      }

      // Send user message first
      onSendMessage({ 
        type: 'question', 
        content: message.trim(),
        threadId
      });

      // Call API and send response
      const response = await apiService.sendMessage(threadId, message.trim());

      // Handle the new API response format
      let content = 'No response from API';
      
      if (response.message) {
        // Don't use the message from the response if it's just repeating the question
        if (response.message === message.trim()) {
          content = ''; // Empty content to avoid repeating the question
        } else {
          content = response.message;
        }
      } else if (response.results && response.results.rows) {
        // Format SQL results as a table if available
        const { columns, rows, rowCount } = response.results;
        
        // Create a formatted representation of the results - without repeating the question
        content = `Results (${rowCount} rows):\n\n`;
        
        // Add the data in a structured format
        if (rows.length > 0) {
          // Create a more readable format for the results
          // For small result sets, format as a table-like structure
          if (rows.length <= 20) {
            // Add column headers
            content += columns.join(' | ') + '\n';
            content += columns.map(() => '---').join(' | ') + '\n';
            
            // Add rows
            rows.forEach(row => {
              const rowValues = columns.map(col => {
                const value = row[col];
                return value !== undefined && value !== null ? String(value) : 'null';
              });
              content += rowValues.join(' | ') + '\n';
            });
          } else {
            // For larger result sets, use JSON format
            content += JSON.stringify(rows, null, 2);
          }
        } else {
          content += "No data returned";
        }
        
        // Add elapsed time if available
        if (response.elapsedTime) {
          content += `\n\nQuery completed in ${response.elapsedTime}`;
        }
      }

      // Check if advanced reasoning is enabled
      const useAdvancedReasoning = localStorage.getItem('useAdvancedReasoning') === 'true' || localStorage.getItem('useAdvancedReasoning') === null;

      if (useAdvancedReasoning) {
        // When advanced reasoning is enabled, wait for interpretation before showing the response
        // This keeps the loading indicator visible until everything is ready
        try {
          // Call the interpret endpoint
          const interpretationResponse = await apiService.interpretThread(threadId, response);

          // Send the bot message with all data (including interpretation)
          // Messages are now stored server-side automatically via POST /threads/{threadId}
          onSendMessage({
            type: 'bot',
            content: content,
            rawResponse: {
              ...response,
              interpretation: interpretationResponse,
              waitingForInterpretation: false
            },
            sql: response.sql,
            threadId
          });
        } catch (interpretError) {
          // Interpretation failed - send response without interpretation
          console.error('Interpretation API failed:', interpretError);
          onSendMessage({
            type: 'bot',
            content: content,
            rawResponse: response,
            sql: response.sql,
            threadId
          });
        }
      } else {
        // Advanced reasoning disabled - send response immediately
        onSendMessage({
          type: 'bot',
          content: content,
          rawResponse: response,
          sql: response.sql,
          threadId
        });
      }

      window.dispatchEvent(new CustomEvent('chatResponseReceived'));

    } catch (error) {
      setError(error.message);
      onSendMessage({ 
        type: 'error', 
        content: `Error: ${error.message}`
      });
      window.dispatchEvent(new CustomEvent('chatResponseReceived'));
    } finally {
      setLocalLoading(false);
      setMessage('');

      window.dispatchEvent(new CustomEvent('chatResponseReceived'));

      // Focus the input after response is complete
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [message, onSendMessage, questionHistoryInstance, isListening, setMessage, setError, setLocalLoading]);

  useEffect(() => {
    // Focus the input when component mounts
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  // Add event listeners for replay functionality
  useEffect(() => {
    const handleSetInput = (e) => {
      const newValue = e.detail.value;
      setMessage(newValue);
      if (inputRef.current) {
        inputRef.current.value = newValue;
      }
    };

    const handleSubmitInput = () => {
      // Create a synthetic event object
      const syntheticEvent = { preventDefault: () => {} };
      handleSubmit(syntheticEvent);
    };

    const handleNewChat = () => {
      localStorage.removeItem('currentThreadId');
      onNewChat?.();
    };

    const handleFocusInput = () => {
      // Focus with a longer delay to ensure it works
      setTimeout(() => {
        try {
          if (inputRef.current) {
            inputRef.current.focus();
            
            // Place cursor at the end of the text
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        } catch (err) {
          // Silently fail
        }
      }, 150);
    };

    window.addEventListener('setInputValue', handleSetInput);
    window.addEventListener('submitInput', handleSubmitInput);
    window.addEventListener('newChat', handleNewChat);
    window.addEventListener('focusChatInput', handleFocusInput);

    return () => {
      window.removeEventListener('setInputValue', handleSetInput);
      window.removeEventListener('submitInput', handleSubmitInput);
      window.removeEventListener('newChat', handleNewChat);
      window.removeEventListener('focusChatInput', handleFocusInput);
    };
  }, [handleSubmit, onNewChat]);

  // PERFORMANCE FIX: Removed expensive MutationObserver approach
  // Focus input using ref-based approach only

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default Enter behavior
      
      // Stop speech recognition if it's active
      if (isListening && recognitionRef.current) {
        recognitionRef.current.stop();
      }
      
      if (message.trim()) {
        handleSubmit(e);
      }
    } else if (e.key === '?' && !message) {
      e.preventDefault();
      setShowSuggestions(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const previousQuestion = questionHistoryInstance.up(message);
      setMessage(previousQuestion);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextQuestion = questionHistoryInstance.down();
      setMessage(nextQuestion);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Store current cursor position
    const selectionStart = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;
    
    // Update state with new value
    setMessage(value);
    
    // After state update, restore cursor position on the next render cycle
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  };

  const handleSelectQuestion = (selectedQuestion) => {
    setMessage(selectedQuestion);
    
    // Focus the input field with a longer delay to ensure it works
    setTimeout(() => {
      try {
        if (inputRef.current) {
          inputRef.current.focus();
          
          // Place cursor at the end of the text
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      } catch (err) {
        // Silently fail
      }
    }, 150);
  };

  const initializeSpeechRecognition = useCallback(() => {
    if (!('webkitSpeechRecognition' in window)) {
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      // Clear existing text when starting new recording
      setMessage('');
    };

    recognition.onend = () => {
      setIsListening(false);
      // Focus the input after speech ends
      inputRef.current?.focus();
    };

    recognition.onresult = (event) => {
      const lastResult = event.results[event.results.length - 1];
      const transcript = lastResult[0].transcript;
      
      // Update both state and input value
      setMessage(transcript);
      if (inputRef.current) {
        inputRef.current.value = transcript;
      }
      
      // If this is the final result, stop listening
      if (lastResult.isFinal) {
        recognitionRef.current?.stop();
        setIsListening(false);
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
  }, []);

  useEffect(() => {
    initializeSpeechRecognition();
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [initializeSpeechRecognition]);

  const toggleSpeechRecognition = () => {
    if (!recognitionRef.current) {
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };

  return (
    <div className={`sticky bottom-0 bg-white border-t transition-all duration-300 ${showSuggestions ? 'mr-80' : ''}`}>
      <QuestionSuggestions
        isOpen={showSuggestions}
        onClose={() => {
          setShowSuggestions(false);
          // Focus the input field when suggestions are closed
          setTimeout(() => {
            try {
              if (inputRef.current) {
                inputRef.current.focus();
                const length = inputRef.current.value.length;
                inputRef.current.setSelectionRange(length, length);
              }
            } catch (err) {
              // Silently fail
            }
          }, 100);
        }}
        onSelectQuestion={handleSelectQuestion}
      />
      <div className="relative">
        {error && (
          <div className="absolute bottom-full left-0 right-0 p-3 bg-red-50 text-red-600 text-sm border-t border-red-200">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-center bg-white border border-gray-300 rounded-2xl px-6 py-4 focus-within:border-gray-500 focus-within:ring-2 focus-within:ring-gray-500/20 transition-all shadow-sm hover:shadow-md">
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              placeholder="Ask anything... (type ? for suggestions)"
              aria-label="Message input"
              aria-describedby="input-hint"
              className="flex-1 bg-transparent outline-none text-gray-900 placeholder-gray-500 text-base disabled:text-gray-400 disabled:cursor-not-allowed"
            />
            <span id="input-hint" className="sr-only">
              Press Enter to send message. Press question mark for suggestions. Use arrow keys to navigate history.
            </span>
            <button
              type="submit"
              disabled={isLoading || !message.trim()}
              aria-label="Send message"
              className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 text-white rounded-full p-2.5 h-10 w-10 flex items-center justify-center ml-3 transition-colors disabled:cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>
      <div className="px-4 pb-2 text-xs text-gray-500 text-center">
        {/* TrackVision workspace chats are never shared with any external services. It can make mistakes. */}
      </div>
    </div>
  );
};

InputBar.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  onNewChat: PropTypes.func
};

InputBar.defaultProps = {
  onNewChat: () => {}
};

export default InputBar; 